import { useEffect, useState } from "react";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { adminFetch } from "./adminApi";

interface AuditLogRow {
  AuditID: number;
  AdminID: number | null;
  AdminUsername?: string;
  Action: string;
  EntityType: string;
  EntityID?: string;
  Reason?: string;
  CreatedAt: string;
}

export default function AdminAuditLog({ onBack }: { onBack: () => void }) {
  const [logs, setLogs] = useState<AuditLogRow[]>([]);
  const [entityType, setEntityType] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const qs = entityType ? `?entityType=${encodeURIComponent(entityType)}` : "";
      const res = await adminFetch(`/api/admin/audit-logs${qs}`);
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.message);
      setLogs(payload.logs || []);
    } catch (e) {
      toast.error("Failed to load audit log.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [entityType]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 rounded-lg bg-[#0c1428] hover:bg-[#0f1e3d] text-[#a0aec0]"><ArrowLeft size={16} /></button>
          <h1 className="text-white text-lg sm:text-xl font-semibold">Audit Log</h1>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            placeholder="Filter by entity type…"
            className="bg-[#0a1020] border border-white/[0.08] text-white rounded-lg px-3 py-2 text-sm placeholder:text-[#3d4f6a]"
          />
          <button onClick={() => void load()} className="p-2 bg-[#0c1428] hover:bg-[#0f1e3d] border border-white/[0.07] rounded-lg text-[#a0aec0]"><RefreshCw size={16} /></button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-[#6b7fa3] text-sm py-14">Loading…</div>
      ) : (
        <div className="bg-[#0c1428] border border-white/[0.07] rounded-2xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#6b7fa3] border-b border-white/[0.06]">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Admin</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Entity</th>
                <th className="px-4 py-3">Reason</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.AuditID} className="border-b border-white/[0.04] text-[#e2e8f0]">
                  <td className="px-4 py-3 text-xs text-[#6b7fa3]">{new Date(l.CreatedAt).toLocaleString()}</td>
                  <td className="px-4 py-3">{l.AdminUsername || (l.AdminID ? `#${l.AdminID}` : "system")}</td>
                  <td className="px-4 py-3 font-mono text-xs">{l.Action}</td>
                  <td className="px-4 py-3 text-xs">{l.EntityType} {l.EntityID}</td>
                  <td className="px-4 py-3 text-xs text-[#a0aec0]">{l.Reason}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-[#6b7fa3]">No audit log entries found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
